/**
 * VoiceRecorder — premium voice-message capture UI.
 *
 * Interaction model (Orange Messages / Telegram-class):
 *   • Tap mic → starts recording immediately (no hold required when
 *     locked from the previous session — see below).
 *   • Hold mic + slide LEFT → cancels.
 *   • Hold mic + slide UP → locks (keep recording without holding).
 *   • Release without slide → sends.
 *
 * Visible elements while recording:
 *   • Live waveform (canvas, repaints from FFT at 60fps).
 *   • Elapsed timer (mono).
 *   • Red pulsing dot indicating "live".
 *   • Cancel / Lock hint chips with gesture-progress feedback.
 *
 * Graceful degradation:
 *   • Permission denied → shows enable-mic banner with deep-link to
 *     browser settings (handled by parent via onPermissionDenied).
 *   • Browser without MediaRecorder → shows offline notice.
 *   • R2 503 from server → parent surfaces "voice messaging offline"
 *     (we just emit the blob; upload is the parent's concern).
 *
 * PersonSimple: aria-pressed on mic button, role="status" on live
 * waveform region, escape key cancels recording.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Microphone, PaperPlaneTilt, Trash, Lock } from "@phosphor-icons/react";
import { useMicrophone } from "./useMicrophone";
import { paintWaveform, formatDuration } from "./waveform";

const FFT_BIN_COUNT = 256;
const CANCEL_THRESHOLD_PX = 80;
const LOCK_THRESHOLD_PX = 60;

export type VoiceRecorderProps = {
  /** Called when the user releases the mic without cancelling. */
  onComplete: (blob: Blob, durationSec: number, mimeType: string) => void;
  /** Called when the user cancels via slide-left or trash button. */
  onCancel?: () => void;
  /** Maximum recording duration in seconds (auto-stop). Default 180 (3min). */
  maxDurationSec?: number;
  /** Accessible label for the mic button (i18n'd by caller). */
  micLabel: string;
  /** Translated chip / hint strings. */
  hints: {
    slideToCancel: string;
    slideToLock: string;
    locked: string;
    permissionDenied: string;
    unsupported: string;
  };
  /** Disable the mic (e.g. while upload in flight). */
  disabled?: boolean;
  testId?: string;
};

type GestureState =
  | { kind: "idle" }
  | { kind: "pressing"; startX: number; startY: number; offsetX: number; offsetY: number; locked: boolean };

export function VoiceRecorder({
  onComplete,
  onCancel,
  maxDurationSec = 180,
  micLabel,
  hints,
  disabled = false,
  testId = "voice-recorder",
}: VoiceRecorderProps) {
  const { t } = useTranslation("common");
  const mic = useMicrophone();
  const [gesture, setGesture] = useState<GestureState>({ kind: "idle" });
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fftBufferRef = useRef<Uint8Array>(new Uint8Array(FFT_BIN_COUNT));
  const paintRafRef = useRef<number | null>(null);

  const isRecording = mic.status === "recording";
  const isLocked = gesture.kind === "pressing" && gesture.locked;
  const isPressing = gesture.kind === "pressing" && !gesture.locked;

  // Live waveform paint loop while recording.
  useEffect(() => {
    if (!isRecording) {
      if (paintRafRef.current !== null) {
        cancelAnimationFrame(paintRafRef.current);
        paintRafRef.current = null;
      }
      return;
    }
    const paint = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const written = mic.getFftSnapshot(fftBufferRef.current);
        // Normalise Uint8 (0..255) to Float (0..1) inline so paintWaveform
        // doesn't have to know about the bin format.
        const peaks: number[] = new Array(written);
        for (let i = 0; i < written; i++) peaks[i] = fftBufferRef.current[i] / 255;
        paintWaveform(canvas, peaks, {
          // INDIGO iris bars on a slightly-elevated card surface.
          activeColor: "rgb(var(--accent))",
          inactiveColor: "rgba(var(--accent), 0.32)",
          progress: 1,
          barWidth: 2,
          barGap: 2,
          minHeight: 2,
        });
      }
      paintRafRef.current = requestAnimationFrame(paint);
    };
    paintRafRef.current = requestAnimationFrame(paint);
    return () => {
      if (paintRafRef.current !== null) {
        cancelAnimationFrame(paintRafRef.current);
        paintRafRef.current = null;
      }
    };
  }, [isRecording, mic]);

  // Auto-stop at max duration.
  useEffect(() => {
    if (!isRecording) return;
    if (mic.elapsedSec >= maxDurationSec) {
      void finish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mic.elapsedSec, isRecording, maxDurationSec]);

  // Esc cancels.
  useEffect(() => {
    if (!isRecording) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelRecording();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  // When the recorder transitions to "stopped" with a blob, emit it.
  // The mic hook leaves the blob in state until next start() so we can
  // safely consume it here.
  useEffect(() => {
    if (mic.status === "stopped" && mic.blob && mic.mimeType) {
      onComplete(mic.blob, mic.elapsedSec, mic.mimeType);
      mic.cancel(); // reset to "idle" so the recorder can fire again
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mic.status, mic.blob, mic.mimeType]);

  const cancelRecording = () => {
    mic.cancel();
    setGesture({ kind: "idle" });
    onCancel?.();
  };

  const finish = async () => {
    await mic.stop();
    setGesture({ kind: "idle" });
  };

  const handlePointerDown: React.PointerEventHandler<HTMLButtonElement> = async (e) => {
    if (disabled) return;
    // If already locked from a prior session, releasing here finishes —
    // otherwise we treat this as a hold-to-record gesture start.
    if (isLocked) return;
    if (mic.status === "denied") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setGesture({
      kind: "pressing",
      startX: e.clientX,
      startY: e.clientY,
      offsetX: 0,
      offsetY: 0,
      locked: false,
    });
    await mic.start();
  };

  const handlePointerMove: React.PointerEventHandler<HTMLButtonElement> = (e) => {
    setGesture((prev) => {
      if (prev.kind !== "pressing" || prev.locked) return prev;
      return {
        ...prev,
        offsetX: e.clientX - prev.startX,
        offsetY: e.clientY - prev.startY,
      };
    });
  };

  const handlePointerUp: React.PointerEventHandler<HTMLButtonElement> = (e) => {
    if (gesture.kind !== "pressing" || gesture.locked) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (gesture.offsetX < -CANCEL_THRESHOLD_PX) {
      cancelRecording();
      return;
    }
    if (gesture.offsetY < -LOCK_THRESHOLD_PX) {
      setGesture((prev) => (prev.kind === "pressing" ? { ...prev, locked: true } : prev));
      return;
    }
    if (isRecording) void finish();
  };

  // Locked-state click handlers: tap send / tap trash.
  const handleSendLocked = () => {
    if (!isRecording) return;
    void finish();
  };
  const handleCancelLocked = () => cancelRecording();

  // ------------- render --------------
  if (mic.status === "denied") {
    return (
      <div
        role="alert"
        data-testid={`${testId}-denied`}
        className="text-[11px] text-warn bg-warn-subtle/40 border border-warn/30 rounded-pill px-3 py-2"
      >
        {hints.permissionDenied}
      </div>
    );
  }
  if (mic.status === "error" && mic.errorMessage === "media_recorder_unsupported") {
    return (
      <div
        role="alert"
        data-testid={`${testId}-unsupported`}
        className="text-[11px] text-fg-muted bg-bg-raised border border-bg-border rounded-pill px-3 py-2"
      >
        {hints.unsupported}
      </div>
    );
  }

  const cancelProgress =
    isPressing && gesture.offsetX < 0
      ? Math.min(1, Math.abs(gesture.offsetX) / CANCEL_THRESHOLD_PX)
      : 0;
  const lockProgress =
    isPressing && gesture.offsetY < 0
      ? Math.min(1, Math.abs(gesture.offsetY) / LOCK_THRESHOLD_PX)
      : 0;

  if (isRecording || isLocked) {
    return (
      <div
        data-testid={`${testId}-active`}
        className="flex items-center gap-2 w-full px-2 py-2 rounded-card bg-bg-raised border border-bg-border"
      >
        {/* Cancel (trash) — visible in locked state; in pressing state
            it morphs from the hint chip below. */}
        {isLocked && (
          <button
            type="button"
            onClick={handleCancelLocked}
            aria-label={t("a11y.cancel", { defaultValue: "Отмена" })}
            data-testid={`${testId}-cancel`}
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-bg-surface text-bad hover:bg-bad-subtle/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bad"
          >
            <Trash size={18} />
          </button>
        )}

        {/* Live indicator + waveform + timer */}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span
            aria-hidden="true"
            className="shrink-0 w-2 h-2 rounded-full bg-bad"
            style={{ animation: "voice-rec-pulse 1.2s ease-in-out infinite" }}
          />
          <canvas
            ref={canvasRef}
            role="status"
            aria-label={t("a11y.recording_waveform")}
            data-testid={`${testId}-waveform`}
            className="flex-1 min-w-0 h-10"
          />
          <span
            data-testid={`${testId}-timer`}
            className="shrink-0 font-mono tabular-nums text-[12px] text-fg-secondary num-display"
          >
            {formatDuration(mic.elapsedSec)}
          </span>
        </div>

        {/* Action button: lock state shows send; pressing state shows
            release-to-send mic again with gesture hint underneath. */}
        {isLocked ? (
          <button
            type="button"
            onClick={handleSendLocked}
            aria-label={t("a11y.send_voice")}
            data-testid={`${testId}-send`}
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent text-accent-on focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <PaperPlaneTilt size={18} />
          </button>
        ) : (
          <div className="shrink-0 flex flex-col items-end gap-0.5">
            <button
              type="button"
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              aria-pressed={true}
              aria-label={micLabel}
              data-testid={`${testId}-mic-pressing`}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent text-accent-on shadow-[0_8px_18px_rgb(var(--accent-rgb) / 0.42)]"
              style={{
                transform: `translate(${Math.min(0, gesture.kind === "pressing" ? gesture.offsetX : 0)}px, ${Math.min(0, gesture.kind === "pressing" ? gesture.offsetY : 0)}px)`,
              }}
            >
              <Microphone size={18} />
            </button>
            <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-fg-muted">
              <span
                style={{ opacity: 1 - lockProgress }}
                aria-hidden={lockProgress > 0.6}
              >
                ← {hints.slideToCancel}
              </span>
              <Lock
                size={9}
                style={{ opacity: 0.4 + lockProgress * 0.6 }}
                aria-hidden="true"
              />
            </div>
          </div>
        )}

        {/* Cancel gesture progress strip — gives the user feedback that
            slide-left has been registered before they pass the threshold. */}
        {isPressing && cancelProgress > 0 && (
          <div
            aria-hidden="true"
            className="absolute left-0 top-0 h-full bg-bad/15 pointer-events-none"
            style={{ width: `${cancelProgress * 100}%` }}
          />
        )}
        <style>{`
          @keyframes voice-rec-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: 0.45; transform: scale(0.75); }
          }
        `}</style>
      </div>
    );
  }

  // Idle: just the mic button.
  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      disabled={disabled || mic.status === "permission"}
      aria-label={micLabel}
      aria-pressed={false}
      data-testid={`${testId}-mic`}
      className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-bg-raised text-fg-secondary hover:text-accent hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <Microphone size={18} />
    </button>
  );
}
